import { useState } from 'react';

type ModalType =
  | 'department'
  | 'subdivision'
  | 'position'
  | 'role'
  | 'skill'
  | 'salaryGrade'
  | 'jobGrade'
  | 'policy'
  | 'mapping'
  | 'delete';

type OrganizationType =
  | 'department'
  | 'subdivision'
  | 'subDivision'
  | 'position'
  | 'role'
  | 'roles'
  | 'skill'
  | 'salaryGrade'
  | 'salarygrade'
  | 'jobGrade'
  | 'jobgrade'
  | 'policy'
  | 'mapping'
  | 'grade';

interface ModalState<T = any> {
  open: boolean;
  data: T | null;
}

interface DeleteDialogState {
  open: boolean;
  type: OrganizationType;
  id: number | string | null;
}

interface DefaultData {
  department: { code: string; name: string; description: string; status: string };
  subdivision: {
    code: string;
    name: string;
    department_id: string;
    description: string;
    status: string;
  };
  position: {
    code: string;
    name: string;
    level: string;
    department_id: string;
    sub_division_id: string;
    role_id: string;
    skill_ids: number[];
    salary_grade_id: string;
    credit_approval_limit: string;
    description: string;
    status: string;
  };
  role: {
    code: string;
    name: string;
    description: string;
    division_ids: number[];
    sub_division_ids: number[];
    position_ids: number[];
    status: string;
  };
  skill: { name: string; category: string; level: string; description: string; status: string };
  salaryGrade: {
    grade: string;
    name: string;
    min_salary: string;
    max_salary: string;
    division_ids: number[];
    sub_division_ids: number[];
    position_ids: number[];
    description: string;
    status: string;
  };
  jobGrade: {
    grade_code: string;
    grade_name: string;
    grade_level: number;
    job_family: string;
    min_salary: number;
    mid_salary: number;
    max_salary: number;
    is_active: boolean;
  };
  policy: {
    policy_code: string;
    policy_name: string;
    effective_date: string;
    calculation_method: string;
    base_salary: number;
    top_salary: number;
    total_grades: number;
    spread: number;
    use_tenure_adjustment: boolean;
    use_competency_adjustment: boolean;
    tenure_increment_percent: number;
    competency_multiplier: Record<string, number>;
    is_active: boolean;
  };
  mapping: { position_id: string; grade_id: string; effective_date: string; is_active: boolean };
}

const defaultData: DefaultData = {
  department: { code: '', name: '', description: '', status: 'active' },
  subdivision: { code: '', name: '', department_id: '', description: '', status: 'active' },
  position: {
    code: '',
    name: '',
    level: '',
    department_id: '',
    sub_division_id: '',
    role_id: '',
    skill_ids: [],
    salary_grade_id: '',
    credit_approval_limit: '',
    description: '',
    status: 'active',
  },
  role: {
    code: '',
    name: '',
    description: '',
    division_ids: [],
    sub_division_ids: [],
    position_ids: [],
    status: 'active',
  },
  skill: { name: '', category: 'technical', level: 'beginner', description: '', status: 'active' },
  salaryGrade: {
    grade: '',
    name: '',
    min_salary: '',
    max_salary: '',
    division_ids: [],
    sub_division_ids: [],
    position_ids: [],
    description: '',
    status: 'active',
  },
  jobGrade: {
    grade_code: '',
    grade_name: '',
    grade_level: 1,
    job_family: 'Staff',
    min_salary: 0,
    mid_salary: 0,
    max_salary: 0,
    is_active: true,
  },
  policy: {
    policy_code: '',
    policy_name: '',
    effective_date: '',
    calculation_method: 'two_point',
    base_salary: 0,
    top_salary: 0,
    total_grades: 10,
    spread: 0.4,
    use_tenure_adjustment: true,
    use_competency_adjustment: true,
    tenure_increment_percent: 3,
    competency_multiplier: { '1': 0.8, '2': 0.9, '3': 1.0, '4': 1.1, '5': 1.2 },
    is_active: true,
  },
  mapping: {
    position_id: '',
    grade_id: '',
    effective_date: new Date().toISOString().split('T')[0],
    is_active: true,
  },
};

interface UseOrganizationModalsReturn {
  modals: {
    deleteDialog: DeleteDialogState;
    departmentModal: ModalState;
    subDivisionModal: ModalState;
    positionModal: ModalState;
    roleModal: ModalState;
    skillModal: ModalState;
    salaryGradeModal: ModalState;
    jobGradeModal: ModalState;
    policyModal: ModalState;
    mappingModal: ModalState;
  };
  openModal: (type: ModalType, item?: any) => void;
  closeModal: (type: ModalType) => void;
  openDeleteDialog: (type: OrganizationType, id: number | string) => void;
}

export default function useOrganizationModals(): UseOrganizationModalsReturn {
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    type: 'department',
    id: null,
  });
  const [departmentModal, setDepartmentModal] = useState<ModalState>({ open: false, data: null });
  const [subDivisionModal, setSubDivisionModal] = useState<ModalState>({ open: false, data: null });
  const [positionModal, setPositionModal] = useState<ModalState>({ open: false, data: null });
  const [roleModal, setRoleModal] = useState<ModalState>({ open: false, data: null });
  const [skillModal, setSkillModal] = useState<ModalState>({ open: false, data: null });
  const [salaryGradeModal, setSalaryGradeModal] = useState<ModalState>({ open: false, data: null });
  const [jobGradeModal, setJobGradeModal] = useState<ModalState>({ open: false, data: null });
  const [policyModal, setPolicyModal] = useState<ModalState>({ open: false, data: null });
  const [mappingModal, setMappingModal] = useState<ModalState>({ open: false, data: null });

  const openModal = (type: ModalType, item: any = null): void => {
    const data = item || (defaultData as any)[type];
    switch (type) {
      case 'department':
        setDepartmentModal({ open: true, data });
        break;
      case 'subdivision':
        setSubDivisionModal({ open: true, data });
        break;
      case 'position':
        setPositionModal({ open: true, data });
        break;
      case 'role':
        setRoleModal({ open: true, data });
        break;
      case 'skill':
        setSkillModal({ open: true, data });
        break;
      case 'salaryGrade':
        setSalaryGradeModal({ open: true, data });
        break;
      case 'jobGrade':
        setJobGradeModal({ open: true, data });
        break;
      case 'policy':
        setPolicyModal({ open: true, data });
        break;
      case 'mapping':
        setMappingModal({ open: true, data });
        break;
    }
  };

  const closeModal = (type: ModalType): void => {
    switch (type) {
      case 'department':
        setDepartmentModal({ open: false, data: null });
        break;
      case 'subdivision':
        setSubDivisionModal({ open: false, data: null });
        break;
      case 'position':
        setPositionModal({ open: false, data: null });
        break;
      case 'role':
        setRoleModal({ open: false, data: null });
        break;
      case 'skill':
        setSkillModal({ open: false, data: null });
        break;
      case 'salaryGrade':
        setSalaryGradeModal({ open: false, data: null });
        break;
      case 'jobGrade':
        setJobGradeModal({ open: false, data: null });
        break;
      case 'policy':
        setPolicyModal({ open: false, data: null });
        break;
      case 'mapping':
        setMappingModal({ open: false, data: null });
        break;
      case 'delete':
        setDeleteDialog({ open: false, type: 'department', id: null });
        break;
    }
  };

  const openDeleteDialog = (type: OrganizationType, id: number | string): void => {
    setDeleteDialog({ open: true, type, id });
  };

  return {
    modals: {
      deleteDialog,
      departmentModal,
      subDivisionModal,
      positionModal,
      roleModal,
      skillModal,
      salaryGradeModal,
      jobGradeModal,
      policyModal,
      mappingModal,
    },
    openModal,
    closeModal,
    openDeleteDialog,
  };
}
